/**
 * Track B — minimal ZIP reader for zip-compressed .lbrn2 files.
 * Detects archives by magic bytes (never by extension), parses the central
 * directory, and inflates entries with the native DecompressionStream
 * ("deflate-raw"), falling back to node:zlib when running under vitest/node.
 * No third-party dependencies.
 */

const LFH_SIG = 0x04034b50; // local file header
const CDH_SIG = 0x02014b50; // central directory header
const EOCD_SIG = 0x06054b50; // end of central directory

/**
 * Hard cap on total decompressed bytes for a single entry, guarding against a
 * malicious zip bomb that would otherwise OOM or hang the browser tab.
 */
const MAX_DECOMPRESSED_BYTES = 256 * 1024 * 1024; // 256 MB

function tooBigError(): Error {
  return new Error(
    "This file expands to more than 256 MB when decompressed, which is far larger than any real " +
      "laser project. It may be corrupt or crafted to exhaust memory. Open it in your laser editor " +
      "and re-save, or pick a different file.",
  );
}

export function isZip(bytes: Uint8Array): boolean {
  if (bytes.length < 4) return false;
  // "PK" then one of: local header 03 04, empty archive 05 06, spanned 07 08.
  if (bytes[0] !== 0x50 || bytes[1] !== 0x4b) return false;
  const c = bytes[2];
  const d = bytes[3];
  return (
    (c === 0x03 && d === 0x04) ||
    (c === 0x05 && d === 0x06) ||
    (c === 0x07 && d === 0x08)
  );
}

export interface ZipEntry {
  name: string;
  /** Decompressed bytes. */
  data(): Promise<Uint8Array>;
}

/** Parse central directory; supports stored (0) and deflate (8) methods. */
export function readZipEntries(bytes: Uint8Array): ZipEntry[] {
  const entries: ZipEntry[] = [];
  const len = bytes.length;
  if (len < 22) return entries;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  const eocd = findEocd(view, len);
  if (eocd >= 0) {
    const count = view.getUint16(eocd + 10, true);
    const cdOffset = view.getUint32(eocd + 16, true);
    let p = cdOffset;
    for (let i = 0; i < count; i++) {
      if (p + 46 > len || view.getUint32(p, true) !== CDH_SIG) break;
      const method = view.getUint16(p + 10, true);
      const compSize = view.getUint32(p + 20, true);
      const uncompSize = view.getUint32(p + 24, true);
      const fnameLen = view.getUint16(p + 28, true);
      const extraLen = view.getUint16(p + 30, true);
      const commentLen = view.getUint16(p + 32, true);
      const localOffset = view.getUint32(p + 42, true);
      const name = decodeName(bytes.subarray(p + 46, p + 46 + fnameLen));
      entries.push(makeEntry(bytes, view, len, name, method, compSize, uncompSize, localOffset));
      p += 46 + fnameLen + extraLen + commentLen;
    }
    if (entries.length) return entries;
  }

  // Fallback: no usable central directory — scan local file headers.
  return scanLocalHeaders(bytes, view, len);
}

/**
 * Locate the end-of-central-directory record. A naive backward scan for the
 * signature can false-positive on arbitrary data that happens to contain the
 * 4 signature bytes (silently yielding zero/garbage entries), so each candidate
 * is validated: its declared comment length must exactly reach end-of-file and
 * the central-directory offset it points at must land on a real CDH signature.
 */
function findEocd(view: DataView, len: number): number {
  const minStart = Math.max(0, len - 22 - 0xffff);
  for (let i = len - 22; i >= minStart; i--) {
    if (view.getUint32(i, true) !== EOCD_SIG) continue;
    if (i + 22 > len) continue;
    const commentLen = view.getUint16(i + 20, true);
    // The comment must run exactly to the end of the file for a real EOCD.
    if (i + 22 + commentLen !== len) continue;
    const count = view.getUint16(i + 10, true);
    const cdOffset = view.getUint32(i + 16, true);
    // The central directory must sit before this record and begin with a CDH
    // signature (empty archives declare zero entries and are allowed to skip it).
    if (count > 0) {
      if (cdOffset + 4 > i || cdOffset + 4 > len) continue;
      if (view.getUint32(cdOffset, true) !== CDH_SIG) continue;
    }
    return i;
  }
  return -1;
}

function scanLocalHeaders(bytes: Uint8Array, view: DataView, len: number): ZipEntry[] {
  const entries: ZipEntry[] = [];
  let p = 0;
  while (p + 30 <= len && view.getUint32(p, true) === LFH_SIG) {
    const method = view.getUint16(p + 8, true);
    const compSize = view.getUint32(p + 18, true);
    const uncompSize = view.getUint32(p + 22, true);
    const fnameLen = view.getUint16(p + 26, true);
    const extraLen = view.getUint16(p + 28, true);
    const name = decodeName(bytes.subarray(p + 30, p + 30 + fnameLen));
    const dataStart = p + 30 + fnameLen + extraLen;
    if (compSize <= 0) break; // data-descriptor sizes are unknown here — give up.
    entries.push(makeEntryAt(bytes, name, method, dataStart, compSize, uncompSize));
    p = dataStart + compSize;
  }
  return entries;
}

function makeEntry(
  bytes: Uint8Array,
  view: DataView,
  len: number,
  name: string,
  method: number,
  compSize: number,
  uncompSize: number,
  localOffset: number,
): ZipEntry {
  return {
    name,
    async data(): Promise<Uint8Array> {
      if (localOffset + 30 > len || view.getUint32(localOffset, true) !== LFH_SIG) {
        throw new Error("zip: bad local header for " + name);
      }
      const fnameLen = view.getUint16(localOffset + 26, true);
      const extraLen = view.getUint16(localOffset + 28, true);
      const dataStart = localOffset + 30 + fnameLen + extraLen;
      return extractData(bytes, method, dataStart, compSize, uncompSize);
    },
  };
}

function makeEntryAt(
  bytes: Uint8Array,
  name: string,
  method: number,
  dataStart: number,
  compSize: number,
  uncompSize: number,
): ZipEntry {
  return {
    name,
    async data(): Promise<Uint8Array> {
      return extractData(bytes, method, dataStart, compSize, uncompSize);
    },
  };
}

async function extractData(
  bytes: Uint8Array,
  method: number,
  dataStart: number,
  compSize: number,
  uncompSize: number,
): Promise<Uint8Array> {
  // Trust the directory-declared uncompressed size only as an early reject; the
  // stream itself is still hard-capped in case the header lies.
  if (uncompSize > MAX_DECOMPRESSED_BYTES) throw tooBigError();
  const comp = bytes.subarray(dataStart, dataStart + compSize);
  if (method === 0) {
    if (comp.length > MAX_DECOMPRESSED_BYTES) throw tooBigError();
    return comp.slice();
  }
  if (method === 8) return inflateRaw(comp);
  throw new Error("zip: unsupported compression method " + method);
}

async function inflateRaw(comp: Uint8Array): Promise<Uint8Array> {
  const DS = (globalThis as { DecompressionStream?: typeof DecompressionStream }).DecompressionStream;
  if (typeof DS === "function") {
    try {
      const ds = new DS("deflate-raw");
      const writer = ds.writable.getWriter();
      const readPromise = readAllChunks(ds.readable);
      // Copy into a standalone buffer so a shared/offset view is never detached.
      // Ignore the writer's backpressure promise so a reader-side abort (past
      // the size cap) can reject readPromise without deadlocking on write().
      void writer.write(comp.slice()).then(
        () => writer.close(),
        () => {},
      );
      return await readPromise;
    } catch (err) {
      if (err instanceof Error && err.message.startsWith("This file expands")) throw err;
      // otherwise fall through to the node path
    }
  }
  const spec = "node:zlib";
  const zlib = (await import(/* @vite-ignore */ spec)) as { inflateRawSync(b: Uint8Array): Uint8Array };
  const out = zlib.inflateRawSync(comp);
  if (out.length > MAX_DECOMPRESSED_BYTES) throw tooBigError();
  return new Uint8Array(out);
}

async function readAllChunks(readable: ReadableStream<Uint8Array>): Promise<Uint8Array> {
  const reader = readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      total += value.length;
      if (total > MAX_DECOMPRESSED_BYTES) {
        // Abort the stream so the decompressor stops producing output, then
        // fail loudly rather than growing memory without bound.
        await reader.cancel().catch(() => {});
        throw tooBigError();
      }
      chunks.push(value);
    }
  }
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}

function decodeName(raw: Uint8Array): string {
  const TD = (globalThis as { TextDecoder?: typeof TextDecoder }).TextDecoder;
  if (typeof TD === "function") return new TD("utf-8").decode(raw);
  let s = "";
  for (let i = 0; i < raw.length; i++) s += String.fromCharCode(raw[i]);
  return s;
}
